import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    Inventory as ReceiveIcon,
    Cancel as CancelIcon,
    TrendingUp as MarginIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../../services/purchaseOrderService';
import { useCurrency } from '../../../hooks/useCurrency';

const PurchaseOrderDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [po, setPO] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPO();
    }, [id]);

    const fetchPO = async () => {
        setLoading(true);
        try {
            const data = await purchaseOrderService.getPurchaseOrderById(id);
            setPO(data);
        } catch (error) {
            toast.error(t('purchaseOrders.fetchError'));
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        try {
            await purchaseOrderService.sendPurchaseOrder(id);
            toast.success(t('purchaseOrders.statusSuccess'));
            fetchPO();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const handleReceive = async () => {
        try {
            await purchaseOrderService.receivePurchaseOrder(id);
            toast.success(t('purchaseOrders.statusSuccess'));
            fetchPO();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const handleCancel = async () => {
        try {
            await purchaseOrderService.cancelPurchaseOrder(id);
            toast.success(t('purchaseOrders.statusSuccess'));
            fetchPO();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            DRAFT: 'default',
            SENT: 'info',
            RECEIVED: 'success',
            CANCELLED: 'error',
        };
        return colors[status] || 'default';
    };

    const { format: formatCurrency } = useCurrency();

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>{t('common.loading')}</Typography>
            </Box>
        );
    }

    if (!po) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Purchase order not found</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchase-orders')}>
                        {t('common.back')}
                    </Button>
                    <Typography variant="h4">{po.poNumber}</Typography>
                    <Chip label={t(`purchaseOrders.status.${po.status}`, po.status)}color={getStatusColor(po.status)} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Editable while DRAFT (fully) or SENT (notes only) — see
                        PurchaseOrderForm's status-gated fields. Hidden for
                        RECEIVED/CANCELLED, which can no longer be modified. */}
                    {(po.status === 'DRAFT' || po.status === 'SENT') && (
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/purchase-orders/edit/${id}`)}
                        >
                            {t('common.edit')}
                        </Button>
                    )}

                    {po.status === 'DRAFT' && (
                        <Button variant="outlined" startIcon={<SendIcon />} onClick={handleSend}>
                            {t('purchaseOrders.updateStatus')}
                        </Button>
                    )}

                    {(po.status === 'DRAFT' || po.status === 'SENT') && (
                        <Button variant="contained" startIcon={<ReceiveIcon />} onClick={handleReceive}>
                            {t('purchaseOrders.status.RECEIVED')}
                        </Button>
                    )}

                    {po.status !== 'RECEIVED' && po.status !== 'CANCELLED' && (
                        <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                            {t('common.cancel')}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* PO Info */}
            <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('purchaseOrders.supplier')}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('purchaseOrders.supplier')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2" fontWeight="medium">
                                        {po.supplierName}
                                    </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('purchaseOrders.expectedDate')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2">
                                        {formatDate(po.expectedDeliveryDate)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.currency')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2">{po.currencyCode}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('purchaseOrders.viewPO')}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('stockMovements.createdBy')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2">{po.createdByUserName}</Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.createdAt')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2">{formatDate(po.createdAt)}</Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('purchaseOrders.totalAmount')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="h6" color="primary">
                                        {formatCurrency(po.totalAmount)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Notes */}
                {po.notes && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('common.notes')}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {po.notes}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Line Items */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Line Items
                            </Typography>
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('stockMovements.product')}</TableCell>
                                            <TableCell>{t('stockMovements.variant')}</TableCell>
                                            <TableCell align="right">{t('purchaseOrders.quantityOrdered')}</TableCell>
                                            <TableCell align="right">{t('purchaseOrders.quantityReceived')}</TableCell>
                                            <TableCell align="right">{t('purchaseOrders.unitCost')}</TableCell>
                                            <TableCell align="right">{t('common.price')}</TableCell>
                                            <TableCell align="right">{t('common.total')}</TableCell>
                                            <TableCell align="right">{t('purchaseOrders.subtotal')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {po.items?.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{item.variantName}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.variantSku}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{item.quantityOrdered}</TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={item.quantityReceived}
                                                        size="small"
                                                        color={
                                                            item.quantityReceived === item.quantityOrdered
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitCost)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.sellPrice)}</TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                                        <MarginIcon
                                                            fontSize="small"
                                                            color={item.margin >= 0 ? 'success' : 'error'}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color={item.margin >= 0 ? 'success.main' : 'error.main'}
                                                            fontWeight="medium"
                                                        >
                                                            {formatCurrency(item.margin)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                    {formatCurrency(item.totalCost)}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold' }}>
                                                {t('common.total')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                {formatCurrency(po.totalAmount)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PurchaseOrderDetails;